import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// TODO: Import your actual types
import type {
  Warband,
  Model,
  Item,
  ExplorationLocation,
  ModelTemplate,
  ItemTemplate
} from '../types';

import {
  useAutoSaveWarband,
  useAutoSaveModels,
  useAutoSaveItems,
  useAutoSaveLocations,
  useAutoSaveTemplates
} from '../hooks/useAutoSave';

import { useToast } from '../components/Toast';

interface GameContextType {
  // State
  warband: Warband;
  models: Model[];
  items: Item[];
  locations: ExplorationLocation[];
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];

  // Warband actions
  updateWarband: (updates: Partial<Warband>) => void;
  addExplorationSkill: (skill: string) => void;
  advanceBattle: (deadModelIds: string[], injuries: {[modelId: string]: string}) => void;

  // Model actions
  addModel: (model: Model) => void;
  updateModel: (id: string, updates: Partial<Model>) => void;
  deleteModel: (id: string) => void;

  // Item actions
  addItem: (item: Item) => void;
  updateItem: (id: string, updates: Partial<Item>) => void;
  deleteItem: (id: string) => void;

  // Location actions
  addLocation: (location: ExplorationLocation) => void;
  updateLocation: (id: string, updates: Partial<ExplorationLocation>) => void;

  // Template actions
  addModelTemplate: (template: ModelTemplate) => void;
  updateModelTemplate: (id: string, updates: Partial<ModelTemplate>) => void;
  deleteModelTemplate: (id: string) => void;
  addItemTemplate: (template: ItemTemplate) => void;
  updateItemTemplate: (id: string, updates: Partial<ItemTemplate>) => void;
  deleteItemTemplate: (id: string) => void;
}

const GameContext = createContext<GameContextType | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();

  // State - initialized with empty/default values
  // These will be populated by the profile loading system
  const [warband, setWarband] = useState<Warband>({
    ducats: 700,
    glory: 0,
    battles: 0,
    explorationSkills: ["Re-roll"],
    name: "My Warband",
    warchest: []
  });
  const [models, setModels] = useState<Model[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [locations, setLocations] = useState<ExplorationLocation[]>([]);
  const [modelTemplates, setModelTemplates] = useState<ModelTemplate[]>([]);
  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>([]);

  // Auto-save hooks (from previous phase)
  useAutoSaveWarband(warband);
  useAutoSaveModels(models);
  useAutoSaveItems(items);
  useAutoSaveLocations(locations);
  useAutoSaveTemplates(modelTemplates, itemTemplates);

  // Warband actions
  const updateWarband = useCallback((updates: Partial<Warband>) => {
    setWarband(prev => ({ ...prev, ...updates }));
  }, []);

  const addExplorationSkill = useCallback((skill: string) => {
    setWarband(prev => ({
      ...prev,
      explorationSkills: [...prev.explorationSkills, skill]
    }));
    showToast(`Exploration skill "${skill}" added`, 'success');
  }, [showToast]);

  const advanceBattle = useCallback((deadModelIds: string[], injuries: {[modelId: string]: string}) => {
    // Mark dead models
    setModels(prev => prev.map(model =>
      deadModelIds.includes(model.id)
        ? { ...model, alive: false, diedOnBattle: prev.find(m => m.id === model.id)?.diedOnBattle || warband.battles + 1 }
        : model
    ));

    // Apply injuries to models
    setModels(prev => prev.map(model =>
      injuries[model.id]
        ? { ...model, scars: [...(model.scars || []), injuries[model.id]] }
        : model
    ));

    // Increment battle count
    setWarband(prev => ({
      ...prev,
      battles: prev.battles + 1
    }));

    showToast(`Advanced to battle ${warband.battles + 1}`, 'info');
  }, [warband.battles, showToast]);

  // Model actions
  const addModel = useCallback((model: Model) => {
    setModels(prev => [...prev, model]);
    showToast('Model added to warband', 'success');
  }, [showToast]);

  const updateModel = useCallback((id: string, updates: Partial<Model>) => {
    setModels(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  const deleteModel = useCallback((id: string) => {
    setModels(prev => prev.filter(m => m.id !== id));
    showToast('Model removed from warband', 'info');
  }, [showToast]);

  // Item actions
  const addItem = useCallback((item: Item) => {
    setItems(prev => [...prev, item]);
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<Item>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
  }, []);

  const deleteItem = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  // Location actions
  const addLocation = useCallback((location: ExplorationLocation) => {
    setLocations(prev => [...prev, location]);
  }, []);

  const updateLocation = useCallback((id: string, updates: Partial<ExplorationLocation>) => {
    setLocations(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  }, []);

  // Template actions
  const addModelTemplate = useCallback((template: ModelTemplate) => {
    setModelTemplates(prev => [...prev, template]);
    showToast('Model template added', 'success');
  }, [showToast]);

  const updateModelTemplate = useCallback((id: string, updates: Partial<ModelTemplate>) => {
    setModelTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const deleteModelTemplate = useCallback((id: string) => {
    setModelTemplates(prev => prev.filter(t => t.id !== id));
    showToast('Model template deleted', 'info');
  }, [showToast]);

  const addItemTemplate = useCallback((template: ItemTemplate) => {
    setItemTemplates(prev => [...prev, template]);
    showToast('Item template added', 'success');
  }, [showToast]);

  const updateItemTemplate = useCallback((id: string, updates: Partial<ItemTemplate>) => {
    setItemTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const deleteItemTemplate = useCallback((id: string) => {
    setItemTemplates(prev => prev.filter(t => t.id !== id));
    showToast('Item template deleted', 'info');
  }, [showToast]);

  const value: GameContextType = {
    // State
    warband,
    models,
    items,
    locations,
    modelTemplates,
    itemTemplates,

    // Actions
    updateWarband,
    addExplorationSkill,
    advanceBattle,
    addModel,
    updateModel,
    deleteModel,
    addItem,
    updateItem,
    deleteItem,
    addLocation,
    updateLocation,
    addModelTemplate,
    updateModelTemplate,
    deleteModelTemplate,
    addItemTemplate,
    updateItemTemplate,
    deleteItemTemplate,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// Hook to use the context
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
}

// Helper function to load profile data into context
export function loadProfileIntoContext(
  context: GameContextType,
  profileData: {
    warband: Warband;
    models: Model[];
    items: Item[];
    locations: ExplorationLocation[];
    modelTemplates: ModelTemplate[];
    itemTemplates: ItemTemplate[];
  }
) {
  // This function allows loading profile data directly into the context
  // bypassing the individual setter functions for bulk loading
  // Note: This is a bit of a hack, but necessary for profile loading
  // In a real app, you'd want to use a reducer pattern or similar

  // Update each piece of state directly
  (context as any).warband = profileData.warband;
  (context as any).models = profileData.models;
  (context as any).items = profileData.items;
  (context as any).locations = profileData.locations;
  (context as any).modelTemplates = profileData.modelTemplates;
  (context as any).itemTemplates = profileData.itemTemplates;
}
